/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-argument, typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'
import { aborted } from '../core/util'
import * as errors from '../core/errors'
import * as types from '../core/types'

// Union schema definition
export interface UnionSchemaDef<
  T extends readonly [types.Schema, types.Schema, ...types.Schema[]],
>
  extends types.SchemaTypeDef {
  type: 'union'
  options: T
}

export interface UnionSchemaInternals<
  T extends readonly [types.Schema, types.Schema, ...types.Schema[]],
> extends types.SchemaInternals<
  types.output<T[number]>,
  types.input<T[number]>
> {
  def: UnionSchemaDef<T>
}

export interface UnionSchema<
  T extends readonly [types.Schema, types.Schema, ...types.Schema[]],
> extends types.Schema<
  types.output<T[number]>,
  types.input<T[number]>,
  UnionSchemaInternals<T>
> {
  readonly options: T
  meta(metadata: Record<string, any>): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  parse(data: unknown, params?: types.ParseContext): types.output<T[number]>
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: types.output<T[number]> }
    | { success: false; error: errors.SchemaError }
}

export const UnionSchema = types.$constructor<UnionSchema<any>>(
  'UnionSchema',
  (inst, def: UnionSchemaDef<any>) => {
    BaseSchema.init(inst, def)

    inst._zod.optin = def.options.some((option: types.Schema) => option._zod.optin === 'defaulted') ? 'defaulted' : def.options.some((option: types.Schema) => option._zod.optin !== undefined) ? 'optional' : undefined
    inst._zod.optout = def.options.some((option: types.Schema) => option._zod.optout === 'optional') ? 'optional' : undefined

    // Port of pinned Zod $ZodUnion and handleUnionResults: start branches in
    // order, accept synchronous success immediately, otherwise await all.
    inst._zod.parse = (payload, ctx) => {
      const results: (types.ParsePayload | Promise<types.ParsePayload>)[] = []
      const finish = (resolved: types.ParsePayload[]) => {
        const success = resolved.find(result => result.issues.length === 0)
        if (success) { payload.value = success.value; return payload }
        const nonaborted = resolved.filter(result => !aborted(result))
        if (nonaborted.length === 1) return nonaborted[0]
        payload.issues.push({ code: 'invalid_union', input: payload.value, inst, errors: resolved.map(result => result.issues.map(issue => errors.formatIssue(issue, ctx.error))) })
        return payload
      }
      let hasAsync = false
      for (const option of def.options) {
        const result: types.ParsePayload | Promise<types.ParsePayload> = option._zod.run({ value: payload.value, issues: [] }, ctx)
        if (result instanceof Promise) hasAsync = true
        else if (result.issues.length === 0) return result
        results.push(result)
      }
      if (hasAsync) return Promise.all(results.map(result => Promise.resolve(result))).then(finish)
      return finish(results as types.ParsePayload[])
    }

    Object.defineProperty(inst, 'options', {
      value: def.options,
      enumerable: true,
    })
  },
)

export function union<
  T extends readonly [types.Schema, types.Schema, ...types.Schema[]],
>(options: T): UnionSchema<T> {
  return new UnionSchema({
    type: 'union',
    options,
  })
}

// Discriminated union schema
export interface DiscriminatedUnionSchemaDef<
  K extends string,
  T extends readonly [types.Schema, types.Schema, ...types.Schema[]],
>
  extends types.SchemaTypeDef {
  type: 'discriminated_union'
  discriminator: K
  options: T
}

export interface DiscriminatedUnionSchema<
  _K extends string,
  T extends readonly [types.Schema, types.Schema, ...types.Schema[]],
> extends types.Schema<types.output<T[number]>, types.input<T[number]>> {
  meta(metadata: Record<string, any>): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  parse(data: unknown, params?: types.ParseContext): types.output<T[number]>
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: types.output<T[number]> }
    | { success: false; error: errors.SchemaError }
}

export const DiscriminatedUnionSchema: types.$constructor<
  DiscriminatedUnionSchema<any, any>,
  DiscriminatedUnionSchemaDef<any, any>
> = types.$constructor(
  'DiscriminatedUnionSchema',
  (
    inst: DiscriminatedUnionSchema<any, any>,
    def: DiscriminatedUnionSchemaDef<any, any>,
  ) => {
    BaseSchema.init(inst, def)

    // Build discriminator map
    const discriminatorMap = new Map<any, types.Schema>()
    for (const option of def.options) {
      // Assume option is an object schema with the discriminator key
      if (
        option._zod.def.type === 'object' &&
        option._zod.def.shape[def.discriminator]
      ) {
        const discriminatorSchema = option._zod.def.shape[def.discriminator]
        // If it's a literal, use its value
        if (discriminatorSchema._zod.def.type === 'literal') {
          discriminatorMap.set(discriminatorSchema._zod.def.value, option)
        }
      }
    }

    inst._zod.parse = (payload, ctx) => {
      if (typeof payload.value !== 'object' || payload.value === null) {
        payload.issues.push({
          code: 'invalid_type',
          expected: 'object',
          input: payload.value,
          inst,
        })
        return payload
      }

      const discriminatorValue = (payload.value as any)[def.discriminator]
      const option = discriminatorMap.get(discriminatorValue)

      if (!option) {
        payload.issues.push({
          code: 'invalid_union',
          errors: [],
          note: 'No matching discriminator',
          discriminator: def.discriminator,
          options: [...discriminatorMap.keys()],
          path: [def.discriminator],
          input: payload.value,
          inst,

        })
        return payload
      }

      return option._zod.run(payload, ctx)
    }
  },
)

export function discriminatedUnion<
  K extends string,
  T extends readonly [types.Schema, types.Schema, ...types.Schema[]],
>(discriminator: K, options: T): DiscriminatedUnionSchema<K, T> {
  return new DiscriminatedUnionSchema({
    type: 'discriminated_union',
    discriminator,
    options,
  })
}
